// 学生端专用 API 服务
// 使用环境变量或默认生产地址，确保与其他端一致
const API_BASE_URL = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) || ''

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: {
    name: string
    code: string
    message: string
    details?: any
  }
  message?: string
}

// 获取认证信息
function getAuthData(): { token: string; refreshToken: string } {
  const authData = localStorage.getItem('talknect-student-auth')
  if (authData) {
    try {
      const parsed = JSON.parse(authData)
      return {
        token: parsed?.state?.token || parsed?.token || '',
        refreshToken: parsed?.state?.refreshToken || parsed?.refreshToken || ''
      }
    } catch {
      return { token: '', refreshToken: '' }
    }
  }
  return { token: '', refreshToken: '' }
}

// 获取认证令牌
function getAuthToken(): string {
  const token = getAuthData().token
  console.log('[API] getAuthToken called, token:', token ? `${token.substring(0, 20)}...` : 'EMPTY')
  return token
}

// 处理认证失效
function handleAuthExpired() {
  localStorage.removeItem('talknect-student-auth')
  const currentPath = window.location.pathname
  if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
    window.location.href = '/login?expired=true'
  }
}

// 保存新的认证信息
function saveAuthData(token: string, refreshToken?: string) {
  const existing = localStorage.getItem('talknect-student-auth')
  let authData = existing ? JSON.parse(existing) : { state: {} }
  if (!authData.state) authData.state = {}
  authData.state.token = token
  if (refreshToken) {
    authData.state.refreshToken = refreshToken
  }
  localStorage.setItem('talknect-student-auth', JSON.stringify(authData))
}

// 检查并刷新令牌（在操作过程中延续登录时间）
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

async function tryRefreshToken(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  const { refreshToken } = getAuthData()
  if (!refreshToken) {
    return false
  }

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data?.token) {
          saveAuthData(data.data.token, data.data.refreshToken)
          return true
        }
      }
      return false
    } catch (error) {
      return false
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// 在成功请求后检查是否需要刷新令牌（基于请求频率）
let lastRefreshTime = 0
const REFRESH_INTERVAL = 30 * 60 * 1000 // 每30分钟检查一次是否需要刷新

function checkAndRefreshToken() {
  const now = Date.now()
  if (now - lastRefreshTime > REFRESH_INTERVAL) {
    const { refreshToken } = getAuthData()
    if (refreshToken) {
      lastRefreshTime = now
      // 异步刷新，不阻塞当前请求
      tryRefreshToken().catch(() => { })
    }
  }
}

// 通用请求函数
async function request<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getAuthToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(options.headers as Record<string, string> || {})
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // 确保路径以 /api/v1 开头
  const apiPath = endpoint.startsWith('/api/v1') ? endpoint : `/api/v1${endpoint}`
  const url = `${API_BASE_URL}${apiPath}`

  // 判断是否是登录/注册接口（这些接口的401不应该触发会话过期处理）
  const isAuthEndpoint = endpoint.includes('/auth/login') || endpoint.includes('/auth/register')

  try {
    const response = await fetch(url, {
      ...options,
      headers
    })

    // 处理认证失效
    if (response.status === 401 || response.status === 403) {
      // 先尝试解析后端返回的真实错误信息
      try {
        const errorData = await response.json()

        // 如果是登录/注册接口，直接返回后端的错误信息（如密码错误、账号不存在等）
        if (isAuthEndpoint) {
          return {
            success: false,
            error: errorData.error || {
              name: 'AuthError',
              code: 'AUTH_FAILED',
              message: errorData.message || '手机号或密码错误，请重试'
            },
            message: errorData.message
          }
        }

        // 对于其他接口，尝试刷新令牌
        if (token) {
          const refreshed = await tryRefreshToken()
          if (refreshed) {
            // 刷新成功，重试原请求
            const newToken = getAuthToken()
            const retryResponse = await fetch(url, {
              ...options,
              headers: {
                ...headers,
                'Authorization': `Bearer ${newToken}`
              }
            })
            if (retryResponse.ok) {
              const retryData = await retryResponse.json()
              return retryData
            }
          }

          // 刷新失败或重试失败，跳转到登录页
          handleAuthExpired()
          return {
            success: false,
            error: {
              name: 'AuthError',
              code: 'AUTH_EXPIRED',
              message: '您已长时间未操作，请重新登录'
            }
          }
        }

        // 未登录用户访问需要认证的接口
        return {
          success: false,
          error: errorData.error || {
            name: 'AuthError',
            code: 'AUTH_REQUIRED',
            message: '请先登录'
          }
        }
      } catch {
        // JSON解析失败，使用默认错误信息
        if (isAuthEndpoint) {
          return {
            success: false,
            error: {
              name: 'AuthError',
              code: 'AUTH_FAILED',
              message: '手机号或密码错误，请重试'
            }
          }
        }

        if (token) {
          // 尝试刷新令牌
          const refreshed = await tryRefreshToken()
          if (!refreshed) {
            handleAuthExpired()
          }
          return {
            success: false,
            error: {
              name: 'AuthError',
              code: 'AUTH_EXPIRED',
              message: '您已长时间未操作，请重新登录'
            }
          }
        }

        return {
          success: false,
          error: {
            name: 'AuthError',
            code: 'AUTH_REQUIRED',
            message: '请先登录'
          }
        }
      }
    }

    // 处理服务器错误 (500 系列)
    if (response.status >= 500) {
      // 如果是 GET 请求页面核心数据失败，考虑跳转到 500 页面
      if (options.method === 'GET' && !endpoint.includes('/auth/')) {
        window.location.href = '/500';
      }
      return {
        success: false,
        error: {
          name: 'ServerError',
          code: 'SERVER_ERROR',
          message: '服务器繁忙，请稍后再试'
        }
      };
    }

    const data = await response.json()

    // 请求成功，检查是否需要刷新令牌（延续登录时间）
    checkAndRefreshToken()

    return data
  } catch (error) {
    return {
      success: false,
      error: {
        name: 'NetworkError',
        code: 'NETWORK_ERROR',
        message: '网络错误，请检查网络连接'
      }
    }
  }
}

// 学生端 API 客户端
export const api = {
  get: <T = any>(endpoint: string) => request<T>(endpoint, { method: 'GET' }),

  post: <T = any>(endpoint: string, data?: any) => request<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined
  }),

  put: <T = any>(endpoint: string, data?: any) => request<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined
  }),

  delete: <T = any>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),

  // 文件上传
  upload: async <T = any>(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<ApiResponse<T>> => {
    const token = getAuthToken()
    const formData = new FormData()
    formData.append('file', file)

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value))
      })
    }

    const apiPath = endpoint.startsWith('/api/v1') ? endpoint : `/api/v1${endpoint}`
    const url = `${API_BASE_URL}${apiPath}`

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      })

      if (response.status === 401 || response.status === 403) {
        // 尝试刷新令牌
        if (token) {
          const refreshed = await tryRefreshToken()
          if (refreshed) {
            // 刷新成功，重试上传
            const newToken = getAuthToken()
            const retryResponse = await fetch(url, {
              method: 'POST',
              headers: newToken ? { 'Authorization': `Bearer ${newToken}` } : {},
              body: formData
            })
            if (retryResponse.ok) {
              return await retryResponse.json()
            }
          }
          handleAuthExpired()
          return {
            success: false,
            error: {
              name: 'AuthError',
              code: 'AUTH_EXPIRED',
              message: '您已长时间未操作，请重新登录'
            }
          }
        }
        return {
          success: false,
          error: {
            name: 'AuthError',
            code: 'AUTH_REQUIRED',
            message: '请先登录'
          }
        }
      }

      // 请求成功，检查是否需要刷新令牌
      checkAndRefreshToken()

      return await response.json()
    } catch (error) {
      return {
        success: false,
        error: {
          name: 'UploadError',
          code: 'UPLOAD_FAILED',
          message: '上传失败，请重试'
        }
      }
    }
  }
}

// 兼容 studentApi 命名
export const studentApi = api

// 短信验证码 API（公开接口，无需登录）
export const smsApi = {
  // 发送验证码
  sendCode: async (phone: string, purpose: string = 'register'): Promise<ApiResponse<{
    expires_in: number
    remaining_ip: number
    remaining_phone: number
  }>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/sms/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, purpose })
      })
      return await response.json()
    } catch (error) {
      return {
        success: false,
        error: {
          name: 'NetworkError',
          code: 'NETWORK_ERROR',
          message: '网络错误，请重试'
        }
      }
    }
  },

  // 验证验证码
  verifyCode: async (phone: string, code: string, purpose: string = 'register'): Promise<ApiResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/sms/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, code, purpose })
      })
      return await response.json()
    } catch (error) {
      return {
        success: false,
        error: {
          name: 'NetworkError',
          code: 'NETWORK_ERROR',
          message: '网络错误，请重试'
        }
      }
    }
  },

  // 检查验证状态
  checkVerified: async (phone: string, purpose: string = 'register'): Promise<ApiResponse<{
    verified: boolean
  }>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/sms/check-verified?phone=${encodeURIComponent(phone)}&purpose=${purpose}`)
      return await response.json()
    } catch (error) {
      return {
        success: false,
        error: {
          name: 'NetworkError',
          code: 'NETWORK_ERROR',
          message: '网络错误，请重试'
        }
      }
    }
  }
}

export default studentApi
